/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Button, Form, DateInput, Icon } from '@mairistem/composants';
import { Section } from '@mairistem/administres-utils';

import type { Mention, OptionCodeAnts } from '@/types';
import { validationInformationsMention } from '@/utils/validations';

interface MentionFormProps {
  onValidate: (mention: Omit<Mention, 'id'>) => void;
  onCancel: () => void;
  numerosExistants?: string[];
  optionsCodeAnts?: OptionCodeAnts[];
}

const DATE_FORMAT = 'DD/MM/YYYY';

const MentionForm = (props: MentionFormProps) => {
  const {
    onValidate,
    onCancel,
    numerosExistants = [],
    optionsCodeAnts = [],
  } = props;

  const [data, setData] = React.useState<Record<string, any>>({});

  const numeroDuplique = React.useMemo(() => {
    const numero = _.trim(_.toString(data?.numero));
    return !_.isEmpty(numero) && _.includes(numerosExistants, numero);
  }, [data?.numero, numerosExistants]);

  const errors = React.useMemo(
    () => validationInformationsMention(data),
    [data],
  );

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => _.set(_.cloneDeep(prev), name, value));
    },
    [],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      setData((prev) =>
        _.set(
          _.cloneDeep(prev),
          'dateApposition',
          _.isNil(date) ? null : dayjs(date).format(DATE_FORMAT),
        ),
      );
    },
    [],
  );

  const handleValidate = React.useCallback(() => {
    onValidate({
      numero: data?.numero ?? '',
      dateApposition: data?.dateApposition ?? '',
      titre: data?.titre ?? '',
      texte: data?.texte ?? '',
      codeAnts: data?.codeAnts ?? '',
    });
  }, [data, onValidate]);

  return (
    <Section title="INFORMATION DE LA MENTION" height="auto">
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <Form.Group>
          <Form.Input
            required
            width={4}
            label="Numéro"
            name="numero"
            value={data?.numero ?? ''}
            error={
              numeroDuplique
                ? 'Ce numéro est déjà utilisé par une autre mention'
                : undefined
            }
            onChange={handleChange}
          />
          <Form.Field width={4}>
            <label>
              Date apposition
              <span style={{ color: '#db2828' }}>&nbsp;*</span>
            </label>
            <DateInput
              value={
                data?.dateApposition
                  ? dayjs(data.dateApposition, DATE_FORMAT).toDate()
                  : undefined
              }
              onChange={handleDateChange}
            />
          </Form.Field>
        </Form.Group>
        <Form.Input
          required
          fluid
          label="Titre"
          name="titre"
          value={data?.titre ?? ''}
          onChange={handleChange}
        />
        <Form.TextArea
          label="Texte"
          name="texte"
          rows={5}
          value={data?.texte ?? ''}
          onChange={handleChange}
        />
        <Form.Dropdown
          required
          selection
          clearable
          fluid
          search
          label="Code ANTS"
          name="codeAnts"
          options={optionsCodeAnts}
          value={data?.codeAnts ?? ''}
          onChange={handleChange}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5em',
            marginTop: '0.5em',
          }}
        >
          <Button data-testid="mention-annuler" onClick={onCancel}>
            <Icon name="close" />
            Annuler
          </Button>
          <Button
            positive
            data-testid="mention-valider"
            disabled={!_.isEmpty(errors) || numeroDuplique}
            onClick={handleValidate}
          >
            <Icon name="check" />
            Valider
          </Button>
        </div>
      </Form>
    </Section>
  );
};

export default MentionForm;
